import {
  ImageRef,
  ApiStatus,
  ApiResponse,
  Dict,
  Entropy,
  RoundRef,
  CountDown,
  UserVote,
  UserImage,
  Winner,
} from "./types";

import { _translateStatusCode } from "./utils";

interface RequestOpts {
  params?: Dict;
  json?: any;
}

export class Api {
  baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async listImages(): Promise<ImageRef[]> {
    const [statusCode, response] = await this._get("/images/");
    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      throw new Error("Unable to query images from backend");
    }

    return response.items as ImageRef[];
  }

  async listRounds(): Promise<RoundRef[]> {
    const [statusCode, response] = await this._get("/rounds/");
    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      throw new Error("Unable to query rounds from backend");
    }
    try {
      return response.items;
    } catch (e) {
      console.log("Error while getting rounds: ", e);
    }
    throw new Error("Unable to query rounds from backend");
  }

  async listWinners(): Promise<Winner[]> {
    const [statusCode, response] = await this._get("/winners/");
    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      throw new Error("Unable to query winners from backend");
    }
    try {
      return response.winners;
    } catch (e) {
      console.log("Error while getting winners: ", e);
    }
    throw new Error("Unable to query winners from backend");
  }

  async countDown(): Promise<CountDown> {
    const [statusCode, response] = await this._get("/rounds/");
    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      throw new Error("Unable to query rounds from backend");
    }
    let countDown: CountDown = {};
    for (const item of response.items) {
      countDown[item.name] = item.end_time;
    }
    return countDown;
  }

  async listEntropy(): Promise<Entropy[]> {
    const [statusCode, response] = await this._get("/entropy/");
    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      throw new Error("Unable to query images from backend");
    }

    try {
      let Entropy: Entropy[] = response.items;
      return Entropy;
    } catch (e) {
      console.log("Error while getting users: ", e);
    }

    throw new Error("Unable to query users from backend");
  }

  async listVotes(round: string): Promise<UserVote[]> {
    const [statusCode, response] = await this._get(
      `/vote/?voting_round=${round}`
    );
    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      throw new Error("Unable to query votes from backend");
    }
    try {
      let votes: UserVote[] = response.items;
      return votes;
    } catch (e) {
      console.log("Error while getting votes: ", e);
    }
    throw new Error("Unable to query votes from backend");
  }

  async listUserImages(address: string): Promise<UserImage[]> {
    const [statusCode, response] = await this._get(
      `/images_user_voted_on/?address=${address}`
    );
    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      throw new Error("Unable to query user images from backend");
    }
    try {
      let images: UserImage[] = response.items;
      return images;
    } catch (e) {
      console.log("Error while getting images: ", e);
    }
    throw new Error("Unable to query images from backend");
  }

  async submitVotes(
    round: string,
    imagesToVoteOn: string[]
  ): Promise<ApiResponse> {
    const [statusCode, response] = await this._post("/vote/", {
      json: {
        rnd: round,
        image_ids: imagesToVoteOn,
      },
    });
    return {
      statusCode: statusCode,
      detail: response?.detail,
      title: response?.title,
    };
  }

  async submitEntropy(entropyToSend: string): Promise<ApiResponse> {
    const [statusCode, response] = await this._post("/entropy/", {
      json: { entropy: entropyToSend },
    });
    return {
      statusCode: statusCode,
      detail: response?.detail,
      title: response?.title,
    };
  }

  async getNonce(ourAddress: string): Promise<string | null> {
    const [statusCode, response] = await this._get(
      `/nonce/?address=${ourAddress}`
    );

    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      return null;
    }

    return response.value;
  }

  async getToken(username: string, password: string): Promise<ApiResponse> {
    const data = new URLSearchParams();
    data.append("username", username);
    data.append("password", password);
    data.append("scope", "write read");
    const [statusCode, response] = await this._post(
      "/token/",
      {},
      data,
      "application/x-www-form-urlencoded"
    );
    if (response?.access_token) {
      localStorage.setItem("access_token", response?.access_token);
    }
    return {
      statusCode: statusCode,
      detail: response?.detail,
      title: response?.title,
    };
  }

  async refreshToken(): Promise<ApiResponse> {
    const [statusCode, response] = await this._post(
      "/token/refresh/",
    );

    if (_translateStatusCode(statusCode) !== ApiStatus.Success) {
      localStorage.removeItem("access_token");
    }
    else {
      localStorage.setItem("access_token", response?.access_token);
    }

    return {
      statusCode: statusCode,
      detail: response?.detail,
      title: response?.title,
    };
  }

  async _get(endpoint: string, opts: RequestOpts = {}): Promise<any> {
    return await this._request("GET", endpoint, opts);
  }

  async _post(
    endpoint: string,
    opts: RequestOpts = {},
    formData?: any,
    contentType?: string
  ): Promise<any> {
    return await this._request("POST", endpoint, opts, formData, contentType);
  }

  async _delete(endpoint: string, opts: RequestOpts = {}): Promise<any> {
    return await this._request("DELETE", endpoint, opts);
  }

  async _request(
    method: string,
    endpoint: string,
    opts?: RequestOpts,
    formData?: any,
    contentType?: string
  ): Promise<[number, string]> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = new Headers();
    headers.append(
      "Content-Type",
      contentType ? contentType : "application/json"
    );
    headers.append(
      "Authorization",
      `Bearer ${localStorage.getItem("access_token")}`
    );

    const response = await fetch(url, {
      method: method,
      headers: headers,
      redirect: "follow",
      mode: "cors",
      referrerPolicy: "no-referrer",
      body: formData ? formData : JSON.stringify(opts?.json ?? undefined),
    });

    return [response.status, await response.json()];
  }
}
